// Scoring and aggregation for game statistics.
//
// Cards are ordered left-to-right by priority, tier 1 being the most urgent.
// Because several cards share a tier there is no single correct arrangement,
// so a position is judged against the tier-sorted sequence rather than against
// specific card identities.

/**
 * Score one 5-card hand from the tiers the player left it in.
 *
 * @param {number[]} tiers tier of each card, in the order the player placed them
 */
export function scoreSnapshot(tiers) {
  const played = Array.isArray(tiers) ? tiers.filter((t) => Number.isFinite(t)) : [];
  const total = played.length;

  if (total === 0) {
    return {
      correct: 0,
      total: 0,
      mistakes: 0,
      longestStreak: 0,
      percentCorrect: 0,
      correctFlags: [],
    };
  }

  const ideal = [...played].sort((a, b) => a - b);
  const correctFlags = played.map((tier, i) => tier === ideal[i]);
  const correct = correctFlags.filter(Boolean).length;

  return {
    correct,
    total,
    mistakes: total - correct,
    longestStreak: longestRun(correctFlags),
    percentCorrect: round1((correct / total) * 100),
    correctFlags,
  };
}

/**
 * Longest unbroken run of correctly placed cards across a whole game.
 *
 * Only three of the five cards are swapped out between deals, so a run is not
 * confined to a single hand. Deals are walked in order and the run carries over
 * the boundary, breaking only where a card is out of position.
 */
export function longestStreakAcrossDeals(deals = []) {
  const flags = [...deals]
    .filter((d) => d && Array.isArray(d.correctFlags))
    .sort((a, b) => (a.roundNumber || 0) - (b.roundNumber || 0))
    .flatMap((d) => d.correctFlags);

  if (flags.length === 0) {
    // Fall back to per-hand figures for deals recorded before flags existed.
    return deals.length ? Math.max(...deals.map((d) => d.longestStreak || 0)) : 0;
  }

  return longestRun(flags);
}

function longestRun(flags) {
  let longest = 0;
  let run = 0;

  flags.forEach((isCorrect) => {
    run = isCorrect ? run + 1 : 0;
    if (run > longest) longest = run;
  });

  return longest;
}

/**
 * Roll a game's deals up into one session summary.
 *
 * @param {Array} deals entries produced by scoreSnapshot plus timing fields
 */
export function aggregateSession(deals = []) {
  const scored = deals.filter((d) => d && d.total > 0);

  const cardsTotal = sum(scored.map((d) => d.total));
  const cardsCorrect = sum(scored.map((d) => d.correct));
  const timed = scored.filter((d) => Number.isFinite(d.durationMs) && d.durationMs > 0);

  return {
    dealsCount: scored.length,
    cardsTotal,
    cardsCorrect,
    mistakes: cardsTotal - cardsCorrect,
    percentCorrect: cardsTotal ? round1((cardsCorrect / cardsTotal) * 100) : 0,
    longestStreak: longestStreakAcrossDeals(scored),
    avgDealMs: timed.length ? Math.round(sum(timed.map((d) => d.durationMs)) / timed.length) : 0,
  };
}

/**
 * Roll a user's saved sessions up into headline figures for the stats page.
 * Accepts rows in the shape returned by the game_sessions table.
 */
export function aggregateHistory(sessions = []) {
  const played = sessions.filter(Boolean);

  if (played.length === 0) {
    return {
      gamesPlayed: 0,
      percentCorrect: 0,
      avgMistakesPerGame: 0,
      avgDealMs: 0,
      avgCardsDealtPerGame: 0,
      longestStreak: 0,
      totalPlayMs: 0,
    };
  }

  const cardsTotal = sum(played.map((s) => s.cards_total));
  const cardsCorrect = sum(played.map((s) => s.cards_correct));

  // Weight by deals so long games count for more than short ones.
  const weighted = played.filter((s) => s.deals_count > 0 && s.avg_deal_ms > 0);
  const weightedDeals = sum(weighted.map((s) => s.deals_count));

  return {
    gamesPlayed: played.length,
    percentCorrect: cardsTotal ? round1((cardsCorrect / cardsTotal) * 100) : 0,
    avgMistakesPerGame: round1(sum(played.map((s) => s.mistakes)) / played.length),
    avgDealMs: weightedDeals
      ? Math.round(sum(weighted.map((s) => s.avg_deal_ms * s.deals_count)) / weightedDeals)
      : 0,
    avgCardsDealtPerGame: round1(sum(played.map((s) => s.cards_dealt)) / played.length),
    longestStreak: Math.max(...played.map((s) => s.longest_streak || 0)),
    totalPlayMs: sum(played.map((s) => s.total_play_ms)),
  };
}

export function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '0s';
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function sum(values) {
  return values.reduce((total, v) => total + (Number(v) || 0), 0);
}

function round1(value) {
  return Math.round(value * 10) / 10;
}
